<template>
  <div class="campaign-spotlight" @click="goToCampaign">
    <div class="campaign-info">
      <div :style="{ color: statusColour }" class="campaign-status">
        <img :src="campaignIcon" class="campaign-icon" />
        {{ ticketStatus }}
      </div>

      <div class="campaign-title">
        {{ ticket.title }}
      </div>

      <div class="campaign-details">
        {{ childTickets }} {{ $t('common.tickets') }} &bull; {{ watchers }}
        {{ $t('common.watchers') }}
        <template v-if="timeLeft">
          &bull; <span class="time-left">{{ timeLeft }}</span>
        </template>
      </div>

      <app-markdown-text
        :sanitize-options="{ ALLOWED_TAGS: ['br', 'p'] }"
        :text="ticket.meta.description"
        class="campaign-description"
      />

      <div class="spacer"></div>

      <div class="campaign-spotlight-bottom">
        <app-link
          :label="$t('menu.learnMore')"
          theme="cta-button"
          @click="goToCampaign"
        />
      </div>
    </div>
    <div
      v-if="campaignImage"
      :style="`background-image: url(${campaignImage});`"
      class="campaign-image"
    />
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import type { ITicket, IWorkflowV2 } from '@rready/sdk';
import {
  getStatusColor,
  getStatusRes,
  useWorkflowProductConfig
} from '@rready/composables';

export default defineComponent({
  props: {
    ticket: {
      type: Object as PropType<ITicket>,
      required: true
    },
    workflow: {
      type: Object as PropType<IWorkflowV2>,
      required: true
    },
    watchers: {
      type: Number,
      default: 0
    },
    childTickets: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: false
    };
  },
  computed: {
    statusColour(): string {
      return getStatusColor(this.ticket.status!, this.workflow) ?? '#7910ff';
    },
    ticketStatus(): string {
      const res = getStatusRes(this.ticket.status!, this.workflow);
      return this.$res(res)?.label ?? this.ticket.status;
    },
    campaignIcon(): string {
      const { icons } = useWorkflowProductConfig(this.workflow);
      return icons?.solid ?? '/img/transparent-pixel.png';
    },
    campaignImage(): string {
      const metaCover = this.ticket.meta.cover;
      if (metaCover) {
        return metaCover;
      }
      const cover = this.ticket.files?.find(
        (file) => file.category === 'ticket-cover'
      );
      return cover?.url || '';
    },
    timeLeft(): string {
      const endDate = this.ticket.meta.campaignEndDate;
      if (!endDate || new Date(endDate) < new Date()) {
        return '';
      }
      const diff = new Date(endDate).getTime() - new Date().getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      return this.$tc('common.duration.days', days);
    }
  },
  methods: {
    goToCampaign(): void {
      this.$router.push({
        name: 'TicketDetails',
        params: {
          id: this.ticket.canonicalId
        }
      });
    },
    displayRelativeDate(date: Date): string {
      const deltaSeconds = Math.round((date.getTime() - Date.now()) / 1000);
      const cutoffs = [60, 3600, 86400, 86400 * 30, 86400 * 365, Infinity];
      const units: Intl.RelativeTimeFormatUnit[] = [
        'second',
        'minute',
        'hour',
        'day',
        'month',
        'year'
      ];
      const unitIndex = cutoffs.findIndex(
        (cutoff) => cutoff > Math.abs(deltaSeconds)
      );
      const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;
      const formatter = new Intl.RelativeTimeFormat(undefined, {
        numeric: 'auto'
      });
      return formatter.format(
        Math.round(deltaSeconds / divisor),
        units[unitIndex]
      );
    }
  }
});
</script>

<style lang="scss" scoped>
@use '@rready/design/dist/assets/utils';

.campaign-spotlight {
  display: flex;
  height: 350px;
  border-radius: var(--radius-2);
  box-shadow: 2px 2px 8px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;

  .campaign-image {
    height: 350px;
    min-width: 500px;
    max-width: 500px;
    display: flex;
    align-items: flex-end;
    background-position: center center;
    background-size: cover;
    border-radius: 0 var(--radius-3) var(--radius-3) 0;
    @media (max-width: 744px) {
      display: none;
    }
  }

  .campaign-info {
    display: flex;
    flex-direction: column;
    width: fit-content;
    flex-grow: 1;
    padding: 24px;
    gap: 14px;
  }

  .campaign-status {
    align-content: center;
    font-size: var(--font-size-tiny);
  }

  .campaign-icon {
    width: 14px;
    height: 14px;
    margin-right: 10px;
  }

  .campaign-title {
    @include utils.line-clamp(2);
    line-height: 30px;
    font-size: 25px;
    font-weight: 600;
    color: var(--color-black);
  }

  .campaign-details {
    display: flex;
    flex-grow: 0;
    font-size: var(--font-size-normal);
    text-transform: capitalize;
    color: var(--color-grey-dark);

    .time-left {
      margin-left: 5px;
      color: var(--color-red);
    }
  }

  .campaign-description {
    @include utils.line-clamp(3);
    font-size: var(--font-size-small);
    font-weight: 500;
    color: var(--color-grey-light-2);
    min-width: 0;
    ::v-deep div {
      white-space: pre-line;
    }
  }

  .spacer {
    flex-grow: 1;
  }

  .campaign-spotlight-bottom {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
}
</style>
